/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionWaitFor';

const ActionWaitForManager: ActionManagerFn<'ActionWaitFor'> = (
  platform: Platform,
): ActionManager<'ActionWaitFor'> => {
  return {
    ...baseActionManager(platform, 'ActionWaitFor'),
    isChildAllowed: (page, item, childType) =>
      ['ActionOnTimeout'].includes(childType),
    defaultValue,
    props: {
      ...baseActionProps,
      waitForCondition: {
        label: 'Wait For Condition',
        type: 'expressionOnly',
        required: true,
        returnType: () => 'boolean',
      },
      timeoutSeconds: {
        label: 'Timeout (Seconds)',
        type: 'numberInput',
        required: true,
      },
    },
    name: 'Wait For',
    defaultChildren: ['ActionOnTimeout'],
    icon: { iconName: 'shipping-timed' },
    description: `**Overview**<br/>
The Wait For action is a synchronization and flow control component designed to pause the execution of an action chain until a specific logical condition is met. Unlike ActionSleep, which waits for a fixed amount of time regardless of the application state, ActionWaitFor intelligently halts progress until the environment is ready (e.g., a variable changes, a record is loaded, or a process completes).<br/><br/>
It includes a built-in safety mechanism via a timeout. If the condition does not become true within the specified time limit, the main action chain is aborted, and a specific "On Timeout" fallback flow is triggered. This prevents the application from hanging indefinitely if an expected state is never reached.

**Key Features**<br/>
* **Conditional Synchronization:** Monitors an expression and blocks subsequent actions from running until that expression evaluates to true.
* **Timeout Safety:** Requires a defined timeoutSeconds limit. If the condition isn't met by then, it stops waiting to prevent infinite loops or frozen UIs.
* **Error Handling:** Explicitly supports ActionOnTimeout as a child component. This allows developers to define a specific set of cleanup or error-handling actions (like showing an error message) that run only if the wait fails.
* **Reactive Evaluation:** Continuously checks the waitForCondition during the wait period.

**Properties**<br/>
* **Wait For Condition (waitForCondition):** Expression (Boolean)
  * *Description:* The logical check that must pass for the action chain to proceed.
  * *Behavior:* The action waits while this is false and proceeds immediately once it becomes true.
* **Timeout (Seconds) (timeoutSeconds):** Number
  * *Description:* The maximum duration to wait for the condition.
  * *Behavior:* If this time elapses and the condition is still false, the main flow stops, and the ActionOnTimeout child is executed.

**Use Cases**<br/>
* **Asynchronous Flags:** Waiting for a global variable isProcessing to become false before allowing a user to click "Save" again.
* **Hardware/Device Integration:** Waiting for a connected printer or scanner to report a status of Ready before sending a print job command.
* **Complex Data Dependencies:** Waiting for a specific calculated value in a Data Store to populate (e.g., OrderTotal > 0) after triggering a complex recalculation script.
* **External Signals:** Polling a state variable that is updated by a WebSocket listener, waiting for a specific "Message Received" signal.`,
  };
};

export default ActionWaitForManager;
